import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { OrbitalInsightsResponseType } from "../../../CommonComponents/orbital-insights/orbital-insights-type"
import { SCENARIO_STEPS, SCENARIO_DEPLOYMENT_TYPES, SCENARIO_OPERATION_TYPES } from "../../constants"
import { SatelliteGroupType, ScenarioResponseType } from "../../types"
import {
    AssetsStatusType,
    FaultPlanDataType,
    FaultReportType,
    FaultsOptionType,
    FormAssetType,
    GeoFeatureOptionType,
    GroundStationOptionType,
    OrbitalInsightsDataType,
    PresimDataType,
    ScenarioFormDataType,
    ScheduleDataType,
    ScheduleTaskType,
    TaskPlanOptionType
} from "./scenario-types"
import { validateSameFaults } from "./validation"

dayjs.extend(utc)

export const getValidEpoch = (...values: Array<number | string | null | undefined>): number => {
    for (const value of values) {
        const epoch = Number(value);
        if (Number.isFinite(epoch) && epoch > 0) return epoch;
    }
    return Math.floor(Date.now() / 1000);
}

export const reValidateScheduleAssets = (schedule: FormAssetType['schedule']) => {
    return {
        ...schedule,
        status: schedule?.data ? 'invalidated' : schedule?.status,
    }
}

export const reValidateFaultsAssets = (faults: FormAssetType['faultPlans']) => {
    return {
        ...faults,
        status: faults?.data ? 'invalidated' : faults?.status,
    }
}

export const formatOrbitalInsightsData = (orbitalInsightsResponse: OrbitalInsightsResponseType | null) => {
    if (!orbitalInsightsResponse) return null
    const orbitalInsights: OrbitalInsightsDataType = {}
    const groups = orbitalInsightsResponse?.satelliteGroups ?? []
    groups.forEach((satelliteGroup) => {
        const satellites = satelliteGroup.satellites ?? []
        satellites.forEach((satellite) => {
            if (!orbitalInsights[satellite?.satelliteId]) {
                orbitalInsights[satellite?.satelliteId] = satellite
            } else {
                orbitalInsights[satellite?.satelliteId] = satellite
            }
        })
    })
    return orbitalInsights
};

export const getFormattedScheduleData = (scheduleResponse: ScheduleDataType[]) => {
    const schedules: { [key: string]: ScheduleTaskType[] } = {}
    scheduleResponse.forEach((group: ScheduleDataType) => {
        const satellites = group?.satellites ?? []
        satellites.forEach(satellite => {
            if (!schedules[satellite?.satId]) {
                schedules[satellite?.satId] = satellite?.scheduledTasks
            }
            schedules[satellite.satId] = satellite?.scheduledTasks
        })
    })
    return schedules
}

export const getFormattedFaultsPlanData = (faultsResponse: FaultPlanDataType[]) => {
    const faults: FaultReportType = {}
    faultsResponse.forEach((fault: FaultPlanDataType) => {
        const satellites = fault?.satellites ?? []
        satellites.forEach(satellite => {
            if (!faults[satellite?.satId]) {
                faults[satellite?.satId] = {
                    faultScheduleId: satellite?.faultScheduleId,
                    faults: satellite?.definedFaultTasks || []
                }
            }
            faults[satellite.satId] = {
                faultScheduleId: satellite?.faultScheduleId,
                faults: satellite?.definedFaultTasks || []
            }
        })
    })
    return faults
}

export const formatPresimData = (presimData: PresimDataType | null, groups: SatelliteGroupType[]) => {
    const updatedPresimData: PresimDataType = {}
    const pannelObject = {
        agilityUrl: [],
        powerUrl: [],
        dataFlowUrl: [],
        thermalUrl: []
    }
    groups.forEach((group: SatelliteGroupType) => {
        if (!group.satellites.length) return
        const satellites = group?.satellites ?? []
        satellites.forEach((sat) => {
            updatedPresimData[sat?.satelliteId] = {
                status: 'idle',
                result: pannelObject,
                orbitDetails: null
            }
        })
    })
    if (presimData) {
        Object.keys(presimData).forEach((key) => {
            updatedPresimData[key] = {
                status: 'invalidated',
                result: presimData[key].result,
                orbitDetails: null
            }
        })
    }
    return updatedPresimData
}

export const resetValidatePresimData = (presimData: PresimDataType | null) => {
    const updatedPresimData: PresimDataType = {}
    if (presimData) {
        Object.keys(presimData).forEach((key) => {
            updatedPresimData[key] = {
                ...presimData[key],
                status: 'invalidated'
            }
        })
    }
    return presimData ? updatedPresimData : presimData
}

export const getHiddenStatus = (
    scenario_step: string,
    scenarioOperationType: string,
    scenarioDeploymentType: string,
    openScenario: boolean
): boolean => {
    switch (scenario_step) {
        case SCENARIO_STEPS.SATELLITES:
            return scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.INITIAL_ALTITUDE:
            return openScenario || scenarioOperationType === SCENARIO_OPERATION_TYPES.CONOPS || scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.HWIL:
            return openScenario || scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.SIMULATE || scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.SEGMENTS:
            return scenarioOperationType === SCENARIO_OPERATION_TYPES.LEOP || scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.SCHEDULE:
            return openScenario || scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.FAULTS:
            return openScenario || scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.ASSETS:
            return openScenario || scenarioOperationType === SCENARIO_OPERATION_TYPES.LEOP || scenarioOperationType === SCENARIO_OPERATION_TYPES.MANUAL
        case SCENARIO_STEPS.FEATURES:
            return true
        default:
            return false
    }
}

export const getConfirmButtonText = (step: string, loading: boolean | string, formData: ScenarioFormDataType, scenario: ScenarioResponseType | null) => {
    const scenarioOperation = formData?.details?.scenarioOperation
    const scheduleStatus = formData?.assets?.schedule?.status as AssetsStatusType

    switch (step) {
        case SCENARIO_STEPS.DETAILS:
            return loading ? loading : scenarioOperation === SCENARIO_OPERATION_TYPES.MANUAL ? 'Deploy' : 'Next'
        case SCENARIO_STEPS.SATELLITES:
            return loading ? loading : 'Next'
        case SCENARIO_STEPS.INITIAL_ALTITUDE:
            return loading ? loading : 'Next'
        case SCENARIO_STEPS.HWIL:
            return loading ? loading : 'Next'
        case SCENARIO_STEPS.SEGMENTS:
            return loading ? loading : 'Next'
        case SCENARIO_STEPS.FEATURES:
            return loading ? loading : 'Next'
        case SCENARIO_STEPS.SCHEDULE:
            return loading ? loading : scheduleStatus === 'completed' ? 'Next' : scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP ? 'Generate Analysis' : 'Generate Schedule'
        case SCENARIO_STEPS.FAULTS:
            return loading ? loading : !scenario ? 'Next' : validateSameFaults(formData?.faults, scenario) ? 'Next' : 'Inject'
        case SCENARIO_STEPS.ASSETS:
            return loading ? loading : 'Generate Config'
        case SCENARIO_STEPS.CONNECT:
            return loading ? loading : 'Deploy & Connect'
        case SCENARIO_STEPS.RECONNECT:
            return loading ? loading : 'Re-Connect'
        case SCENARIO_STEPS.DEPLOY:
            return loading ? loading : 'Deploy'
        case SCENARIO_STEPS.START_SIMULATION:
            return loading ? loading : 'Start Simulation'
        default:
            return true
    }
}

export const getBackButtonText = (step: string, loading: boolean | string) => {
    switch (step) {
        case SCENARIO_STEPS.DETAILS:
            return 'Close'
        case SCENARIO_STEPS.SATELLITES:
            return 'Back'
        case SCENARIO_STEPS.INITIAL_ALTITUDE:
            return 'Back'
        case SCENARIO_STEPS.HWIL:
            return 'Back'
        case SCENARIO_STEPS.SEGMENTS:
            return 'Back'
        case SCENARIO_STEPS.FEATURES:
            return 'Back'
        case SCENARIO_STEPS.SCHEDULE:
            return 'Back'
        case SCENARIO_STEPS.FAULTS:
            return 'Back'
        case SCENARIO_STEPS.ASSETS:
            return 'Back'
        case SCENARIO_STEPS.CONNECT:
            return 'Back'
        case SCENARIO_STEPS.RECONNECT:
            return (loading && loading === 'Undeploying') ? loading : 'Undeploy'
        case SCENARIO_STEPS.DEPLOY:
            return 'Back'
        case SCENARIO_STEPS.START_SIMULATION:
            return (loading && loading === 'Undeploying') ? loading : 'Undeploy'
        default:
            return true
    }
}

export const goPreviousStep = (step: string, formData: ScenarioFormDataType, updateScenarioStep: (step: string) => void, closeScenario: () => void, undeployScenario: () => void) => {
    const scenarioOperationType = formData?.details?.scenarioOperation
    const scenarioDeploymentType = formData?.details?.scenarioDeploymentType

    switch (step) {
        case SCENARIO_STEPS.DETAILS:
            return closeScenario()
        case SCENARIO_STEPS.SATELLITES:
            return updateScenarioStep(SCENARIO_STEPS.DETAILS)
        case SCENARIO_STEPS.INITIAL_ALTITUDE:
            return updateScenarioStep(SCENARIO_STEPS.SATELLITES)
        case SCENARIO_STEPS.HWIL:
            return updateScenarioStep(SCENARIO_STEPS.SATELLITES)
        case SCENARIO_STEPS.SEGMENTS:
            return updateScenarioStep(scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? SCENARIO_STEPS.HWIL : SCENARIO_STEPS.SATELLITES)
        case SCENARIO_STEPS.FEATURES:
            return () => { }
        case SCENARIO_STEPS.SCHEDULE:
            return updateScenarioStep(scenarioOperationType === SCENARIO_OPERATION_TYPES.LEOP ? SCENARIO_STEPS.INITIAL_ALTITUDE : SCENARIO_STEPS.SEGMENTS)
        case SCENARIO_STEPS.FAULTS:
            return updateScenarioStep(SCENARIO_STEPS.SCHEDULE)
        case SCENARIO_STEPS.ASSETS:
            return updateScenarioStep(SCENARIO_STEPS.FAULTS)
        case SCENARIO_STEPS.CONNECT:
            return updateScenarioStep(SCENARIO_STEPS.FAULTS)
        case SCENARIO_STEPS.RECONNECT:
            return undeployScenario()
        case SCENARIO_STEPS.DEPLOY:
            const previousStep = scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER ? SCENARIO_STEPS.SEGMENTS : SCENARIO_STEPS.FAULTS
            return updateScenarioStep(previousStep)
        case SCENARIO_STEPS.START_SIMULATION:
            return undeployScenario()
        default:
            return true
    }
}

export const getUtcFormattedDate = (epochSec: number) => {
    return dayjs.unix(epochSec).utc().format("YYYY-MM-DD[T]HH:mm:ss[Z]");
}


export const getViewerPacketDetails = (formData: ScenarioFormDataType, allTaskPlans: Record<string, TaskPlanOptionType[]>, allGeoFeatures: GeoFeatureOptionType[], allFaults: Record<string, FaultsOptionType[]>, allStations: GroundStationOptionType[]) => {
    const flatTaskplans = (Object.entries(allTaskPlans).map(([_, taskplans]) => {
        return taskplans
    })).flat()
    const flatFaults = (Object.entries(allFaults).map(([_, faults]) => {
        return faults
    })).flat()
    const selectedFaults = new Set(
        (formData?.faults ?? []).map(fault => fault?.faultPlanId)
    );
    const selectedFaultGeofeatures = flatFaults
        .filter(fault => selectedFaults.has(fault?.faultPlanId))
        .flatMap(plan => plan?.faults ?? [])
        .map((f: any) => f?.executionRequirement?.geofeatureId)
        .filter(id => id != null);
    const selectedStations = formData?.segments.stations || []
    const selectedTaskplanIds = (formData?.schedule.taskplans || []).map((tp) => tp?.taskPlanId) || []
    const selectedTaskplans = (flatTaskplans.filter((taskplan) => selectedTaskplanIds.includes(taskplan?.taskPlanId)) || []).map(plan => plan?.taskDetails).flat()
    const selectedFaultPlans = allGeoFeatures.filter((geoFeature) => selectedFaultGeofeatures.includes(geoFeature?.areaOfInterestId)) || []

    return {
        allGroundStations: allStations,
        selectedStations,
        selectedTaskplans,
        selectedFaultPlans
    }
}
